import { useState } from "react";

import { VerifiedSeal, VERIFIED_PILLARS } from "@/components/VerifiedSeal";

/**
 * The "Verified by SkillPatch" badge + an explainer modal.
 *
 * The badge is generic (same for every verified skill), so this lives in one
 * place and is reused on the skill detail page, skill cards, and lists.
 *
 * - `onSeeRubric` — when provided (detail page, where the rubric data exists),
 *   the modal shows a "See this skill's rubric" button that closes this modal
 *   and opens the caller's rubric. Omit it on cards/lists (no rubric there).
 * - `stopPropagation` — set true when the badge sits inside a clickable card so
 *   opening the modal doesn't also navigate the card.
 */
export function VerifiedBadge({
  size = "md",
  onSeeRubric,
  stopPropagation = false,
  iconOnly = false,
}: {
  size?: "sm" | "md";
  onSeeRubric?: () => void;
  stopPropagation?: boolean;
  // Render just the shield-check icon (green, clickable) instead of the full text pill — same modal.
  iconOnly?: boolean;
}) {
  const [open, setOpen] = useState(false);

  const pill =
    size === "sm"
      ? "gap-0.5 px-1.5 py-0.5 text-[10px] font-semibold"
      : "gap-1 px-2.5 py-0.5 text-xs font-medium";
  const check = size === "sm" ? 9 : 11;

  const openModal = (e: React.MouseEvent) => {
    if (stopPropagation) {
      e.preventDefault();
      e.stopPropagation();
    }
    setOpen(true);
  };

  return (
    <>
      {iconOnly ? (
        <button
          type="button"
          title="Verified by SkillPatch — passed our security review"
          onClick={openModal}
          className="flex shrink-0 items-center justify-center text-green-600 transition hover:text-green-500 dark:text-green-400"
          style={{ filter: "drop-shadow(0 0 6px rgba(34,197,94,0.55))" }}
          aria-label="Verified by SkillPatch"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="M9 12l2 2 4-4" />
          </svg>
        </button>
      ) : (
        <button
          type="button"
          title="What does “Verified by SkillPatch” mean?"
          onClick={openModal}
          className={`flex shrink-0 items-center rounded-full border border-green-500/40 bg-green-500/10 text-green-600 shadow-[0_0_10px_rgba(34,197,94,0.45)] transition hover:border-green-500/70 hover:bg-green-500/20 hover:shadow-[0_0_16px_rgba(34,197,94,0.7)] dark:text-green-400 ${pill}`}
        >
          <svg width={check} height={check} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6L9 17l-5-5" />
          </svg>
          Verified by SkillPatch
          {/* trailing info glyph — signals the badge is clickable */}
          <svg className="opacity-70" width={check} height={check} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4M12 8h.01" />
          </svg>
        </button>
      )}

      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
          onClick={() => setOpen(false)}
        >
          <div
            className="flex w-full max-w-lg flex-col rounded-2xl border border-white/10 bg-[#111] shadow-2xl"
            style={{ maxHeight: "90vh" }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl border-2 border-green-500/40 bg-green-500/[0.06] text-green-400">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    <path d="M9 12l2 2 4-4" />
                  </svg>
                </div>
                <h3 className="font-display text-base font-semibold">What “Verified by SkillPatch” means</h3>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-lg text-text/40 transition hover:bg-white/10 hover:text-text"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="overflow-y-auto px-6 py-5">
              <p className="text-sm leading-relaxed text-text/70">
                A skill is just instructions and code your AI agent reads and trusts. Before any
                skill goes live, on every change <span className="font-medium text-text">SkillPatch
                reviews it end&#8209;to&#8209;end: its structure and its security.</span>{" "}
                This badge means it passed both.
              </p>

              <div className="mt-4 flex items-center justify-between gap-2">
                <h4 className="text-sm font-semibold text-text">What we verify</h4>
                <span className="inline-flex items-center gap-1 rounded-full border border-green-500/30 bg-green-500/10 px-2 py-0.5 text-[11px] font-semibold text-green-400">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                  {VERIFIED_PILLARS.length}/{VERIFIED_PILLARS.length} passed
                </span>
              </div>

              {/* Verification seal — one named node per pillar (follows the theme; the
                  modal itself is dark in dark mode / white in light mode) */}
              <div className="mt-3 flex justify-center">
                <VerifiedSeal />
              </div>

              {onSeeRubric && (
                <div className="mt-4 flex justify-center">
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      onSeeRubric();
                    }}
                    className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-3.5 py-1.5 text-xs font-medium text-primary transition hover:border-primary hover:bg-primary/20"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    </svg>
                    View security rubric →
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
